import { PlusCircle } from "@phosphor-icons/react";
import { type JSX, useState } from "react";
import { Route, Routes, useLocation } from "react-router-dom";

import {
  ContentContainer,
  ContentHeader,
  PageTitle,
} from "features/layout/components";
import { TEMPLATE_TYPES } from "features/templates/constants";
import useTemplates from "features/templates/datahooks/useTemplates";

import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import Loader from "common/components/Loader";
import RenderIf from "common/components/RenderIf";
import RestrictedComponent from "common/components/RestrictedComponent";
import SearchInput from "common/components/SearchInput";
import { Button } from "common/components/ui/Button";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { typedObjectEntries } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useTemplatesFilters from "../../hooks/useTemplatesFilters";
import FiltersDropdown from "./FiltersDropdown";
import NewTemplate from "./NewTemplate";
import SelectedTemplate from "./SelectedTemplate";
import SelectTemplateType from "./SelectTemplateType";
import TemplatesList from "./TemplatesList";

import EmptyPlaceholderImage from "assets/images/empty-placeholders/templates.png";

export default function AllTemplates() {
  const isDesktop = useTwBreakpoint("lg");
  const { permissions } = useSelectedWorkspaceContext();
  const { pathname } = useLocation();
  const [isTypeSelectionOpen, setIsTypeSelectionOpen] = useState(false);

  const {
    allTemplates,
    isLoadingAllTemplates,
    templatesError,
    refetchTemplates,
  } = useTemplates();
  const {
    filteredTemplates,
    searchText,
    setSearchText,
    templateType,
    setTemplateType,
    sortOrder,
    setSortOrder,
  } = useTemplatesFilters(allTemplates);

  function onCreateTemplate() {
    setIsTypeSelectionOpen(true);
  }

  let mainContent: JSX.Element;

  const isCreatingTemplate = pathname.includes("/new");
  const isEmpty =
    !isLoadingAllTemplates && !allTemplates?.length && !isCreatingTemplate;

  if (templatesError) {
    mainContent = (
      <ErrorPlaceholder
        errorMessage="Could not display Templates"
        onRetry={refetchTemplates}
      />
    );
  } else if (isEmpty) {
    mainContent = (
      <EmptyPlaceholder
        title="No templates found"
        subtitle="Templates are reusable Notes, InMails and Messages, available across your campaigns"
        imageSrc={EmptyPlaceholderImage}
        actionComponent={
          <RestrictedComponent visibleForPermissions={["templates_full"]}>
            <Button
              leftIcon={<PlusCircle weight="fill" />}
              onClick={onCreateTemplate}
            >
              Create template
            </Button>
          </RestrictedComponent>
        }
      />
    );
  } else {
    mainContent = (
      <>
        <div className="flex flex-col gap-y-2.5 p-4">
          <div className="flex gap-x-2.5">
            <SearchInput
              className="max-lg:flex-1 lg:max-w-80"
              placeholder="Search templates"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              onClear={() => setSearchText("")}
            />
            <FiltersDropdown
              templateType={templateType}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
            />
          </div>
          <div className="flex gap-x-2">
            {typedObjectEntries(TEMPLATE_TYPES).map(([key, { shortLabel }]) => {
              const isSelected = key === templateType;
              return (
                <Button
                  value={key}
                  key={key}
                  size="sm"
                  variant={isSelected ? "default-black" : "secondary-black"}
                  onClick={() => setTemplateType(key)}
                >
                  {shortLabel}
                </Button>
              );
            })}
          </div>
        </div>
        <TemplatesList
          templates={filteredTemplates}
          isLoading={isLoadingAllTemplates}
          templateType={templateType}
          hasSearch={!!searchText}
        />
      </>
    );
  }

  return (
    <>
      <div className="flex gap-x-2 h-full flex-grow min-h-0">
        <ContentContainer
          className={!isEmpty && "lg:max-w-[352px] w-full shrink-0"}
        >
          <ContentHeader>
            <PageTitle />

            <RenderIf condition={!isEmpty}>
              <RestrictedComponent visibleForPermissions={["templates_full"]}>
                <Button
                  leftIcon={<PlusCircle weight="fill" />}
                  onClick={onCreateTemplate}
                  size={isDesktop ? "sm" : "md"}
                >
                  Create template
                </Button>
              </RestrictedComponent>
            </RenderIf>
          </ContentHeader>

          {mainContent}
        </ContentContainer>

        {!isEmpty && (
          <ContentContainer>
            <Routes>
              {permissions.includes("templates_full") && (
                <Route path="/new/:templateType" element={<NewTemplate />} />
              )}
              <Route path="/:templateId" element={<SelectedTemplate />} />
              {isDesktop && (
                <Route
                  path="*"
                  element={
                    isLoadingAllTemplates ? (
                      <Loader />
                    ) : (
                      <EmptyPlaceholder
                        title="No template selected"
                        subtitle="Select an existing template to edit it, or create a new one"
                        imageSrc={EmptyPlaceholderImage}
                      />
                    )
                  }
                />
              )}
            </Routes>
          </ContentContainer>
        )}
      </div>

      <SelectTemplateType
        isOpen={isTypeSelectionOpen}
        setIsOpen={setIsTypeSelectionOpen}
        initialTemplateType={templateType}
      />
    </>
  );
}
